import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function MiningTable17() {
  const pdfRef = useRef();

  const data = [
    {
      education_label: "High school or equivalent, no college",
      male: 2476,
      female: 348,
      employment_gap: 2128,
      male_to_female_ratio: 7.11,
    },
    {
      education_label: "Some college or Associate degree",
      male: 2454,
      female: 435,
      employment_gap: 2019,
      male_to_female_ratio: 5.64,
    },
    {
      education_label: "Bachelor's degree or advanced degree",
      male: 1413,
      female: 308,
      employment_gap: 1105,
      male_to_female_ratio: 4.59,
    },
    {
      education_label: "Less than high school",
      male: 983,
      female: 149,
      employment_gap: 834,
      male_to_female_ratio: 6.6,
    },
    {
      education_label:
        "Educational attainment not available (workers aged 24 or younger)",
      male: 839,
      female: 130,
      employment_gap: 709,
      male_to_female_ratio: 6.45,
    },
  ];

  // Chart Data
  const chartData = {
    labels: data.map((d) => d.education_label),
    datasets: [
      {
        label: "Male Employment",
        data: data.map((d) => d.male),
        backgroundColor: "#0ea5e9",
        borderRadius: 6,
      },
      {
        label: "Female Employment",
        data: data.map((d) => d.female),
        backgroundColor: "#EC4899",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employment by Gender and Education Level (2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Education Level" },
        grid: { display: false },
        ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 45 },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Number of Employees" },
      },
    },
  };

  // Summary Insights
  const totalMale = data.reduce((sum, d) => sum + d.male, 0);
  const totalFemale = data.reduce((sum, d) => sum + d.female, 0);
  const totalGap = totalMale - totalFemale;
  const avgRatio = (
    data.reduce((sum, d) => sum + d.male_to_female_ratio, 0) / data.length
  ).toFixed(2);

  const topGap = data.reduce(
    (max, d) => (d.employment_gap > max.employment_gap ? d : max),
    data[0],
  );

  // PDF Download
    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          const logoWidth = 20; // ideal header width
          const logoHeight = 20; // adjust ratio if needed

          pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY,
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right",
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        // ===== FINAL FOOTER SAFE AREA =====

        const footerTopY = pageHeight - 40; // start footer 40mm from bottom
        const footerContentY = pageHeight - 28; // logos row
        const footerBottomTextY = pageHeight - 10; // bottom text row

        // Top Divider Line
        pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

        // UtahData logo
        const utahWidth = 18;
        const utahHeight = 18;
        pdf.addImage(
          utahData,
          "PNG",
          20,
          footerContentY - 8,
          utahWidth,
          utahHeight,
        );

        // Built by
        pdf.setFontSize(10);
        pdf.text("Built by", 45, footerContentY);

        // BODEX logo
        const bodexWidth = 36;
        const bodexHeight = bodexWidth * 0.3;
        pdf.addImage(
          bodex,
          "PNG",
          60,
          footerContentY - 6,
          bodexWidth,
          bodexHeight,
        );

        // Using text
        pdf.text("using", 102, footerContentY);

        // KPI logo
        pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

        // Version
        pdf.text(APP_VERSION, 140, footerContentY);

        // Bottom Info Line
        pdf.setFontSize(8);
        pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          pageWidth - 10,
          footerBottomTextY,
          null,
          null,
          "right",
        );

        pdf.save("employment_gender_education.pdf");
      });
    };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment by Gender and Education Level (2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Employment Gap
            </p>
            <p className="text-xl font-bold text-blue-800">
              {totalGap.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Largest Gender Gap
            </p>
            <p className="text-sm font-semibold text-yellow-800">
              {topGap.education_label}
            </p>
            <p className="text-lg font-bold text-yellow-800">
              +{topGap.employment_gap.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Avg Male-to-Female Ratio
            </p>
            <p className="text-xl font-bold text-green-800">{avgRatio} : 1</p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          {/* <div className="flex flex-col h-full"> */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Education Level</th>
                  <th className="p-2">Male</th>
                  <th className="p-2">Female</th>
                  <th className="p-2">Gap</th>
                  <th className="p-2">M:F Ratio</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr
                    key={i}
                    className={`${
                      row.education_label === topGap.education_label
                        ? "bg-yellow-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2 text-left">{row.education_label}</td>
                    <td className="p-2">{row.male.toLocaleString()}</td>
                    <td className="p-2">{row.female.toLocaleString()}</td>
                    <td className="p-2">
                      {row.employment_gap.toLocaleString()}
                    </td>
                    <td className="p-2">{row.male_to_female_ratio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* </div> */}

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
